/**
 * Bilingual i18n for IDDL — English & Arabic.
 *
 * The Arabic translation deliberately uses the user-specified phrases:
 *   • "Candidate" → "المتقدم للرخصة"
 *   • "International Digital Driving License" → "الرخصة الدولية للقيادة الرقمية"
 *
 * Usage:  const { t, lang, setLang, dir } = useT();
 * RTL is applied at the <html> level by the LanguageProvider when AR.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const EN = {
  // header
  "header.brand_name": "IDDL",
  "header.brand_sub": "International Digital Driving License",
  "header.switch_lang": "العربية",
  "header.home": "Home",

  // landing
  "landing.badge": "IDDL · LEVEL 1 · BODY OF KNOWLEDGE V1",
  "landing.brand_full": "International Digital Driving License",
  "landing.domain_ds": "Digital Skills",
  "landing.domain_cs": "Cybersecurity Awareness",
  "landing.domain_ai": "AI Competency",
  "landing.tagline": "Digital Capability. Trusted Behaviour. Licensed for Impact.",
  "landing.btn_candidate": "Candidate",
  "landing.btn_candidate_sub": "Begin assessment",
  "landing.btn_admin": "Admin",
  "landing.btn_admin_sub": "Review attempts",
  "landing.pillar_inspire": "INSPIRE",
  "landing.pillar_develop": "DEVELOP",
  "landing.pillar_discover": "DISCOVER",
  "landing.pillar_lead": "LEAD",
  "landing.foot_know": "Know — knowledge",
  "landing.foot_perform": "Perform — behavioural capability",
  "landing.foot_reason": "Reason — reflection",
  "landing.foot_sequence": "3 concepts · DS → CS → AI · locked sequence",
  "landing.foot_result": "Pass / Merit / Fail revealed only at the end",
  "landing.foot_license": "2-year licence · public verification",

  // candidate start
  "start.step": "Step 0 · Open registration",
  "start.title": "Begin your IDDL attempt",
  "start.description": "You will be assessed on three randomly assigned concepts — one each from Digital Skills, Cybersecurity, and AI Competency — in that order. The sequence cannot be skipped. The result is revealed only after all three.",
  "start.label_name": "Full name",
  "start.ph_name": "As it should appear on your licence",
  "start.label_email": "Email · optional",
  "start.ph_email": "you@example.org",
  "start.label_country": "Country",
  "start.ph_country": "e.g. Jordan, New Zealand, United Kingdom",
  "start.label_invite": "Invite code · optional",
  "start.ph_invite": "Leave blank if none",
  "start.label_photo": "Candidate photo",
  "start.upload_photo": "Upload photo",
  "start.remove": "remove",
  "start.fee_label": "Examination fee",
  "start.fee_note": "One-time IDDL Level 1 examination fee. Payment is required to unlock your attempt.",
  "start.pay": "Pay US$ {amount}.00",
  "start.paid": "Paid · US$ {amount}.00",
  "start.btn_start": "Start assessment",
  "start.btn_start_disabled": "Complete required fields & payment",
  "start.required": "Required: name, country, photo, payment.",
  "start.photo_missing": "Photo missing.",
  "start.country_missing": "Country missing.",
  "start.payment_pending": "Payment pending.",
  "start.err_name": "Name is required",
  "start.err_country": "Country is required",
  "start.err_photo": "Please upload a photo",
  "start.err_pay": "Please complete payment to continue",
  "start.starting": "Starting…",

  // attempt runner chrome
  "runner.attempt": "Attempt",
  "runner.sequence": "Sequence",
  "runner.candidate": "Candidate",
  "runner.concept_of": "Concept {n} of {total} ·",
  "runner.step_label": "{step} step",
  "runner.could_not_load": "Could not load attempt",

  // steps — knowledge
  "step.knowledge_title": "Confirm what you know",
  "step.knowledge_sub": "Answer each question. There is exactly one best answer per question.",
  "step.knowledge_layer": "Layer 1 · Knowledge — Know",
  "step.knowledge_submit": "Continue to capability simulation",
  "step.knowledge_submitting": "Submitting…",
  "step.knowledge_q": "Q{n}",
  "step.knowledge_answer_all": "Answer every question before continuing",

  // steps — capability
  "step.capability_layer": "Layer 2 · Behavioural capability (simulation)",
  "step.capability_title": "Behavioural capability — {concept}",
  "step.capability_sub": "Simulation-based behavioural test. Your observed actions inside the simulator are the evidence — not your answer about what you would do.",
  "step.capability_task": "Task",
  "step.capability_events": "{n} behaviour events captured · live",
  "step.capability_end_early": "End simulation early",
  "step.capability_finalize": "Finalize",

  // steps — reflection
  "step.reflection_layer": "Layer 3 · Reflection — Reason",
  "step.reflection_title": "Justify your decisions",
  "step.reflection_sub": "Reflect on the actions you actually took inside the simulator. We score the quality of the reasoning — not the length or wording.",
  "step.reflection_prompt": "Prompt",
  "step.reflection_placeholder": "Walk through your reasoning. Reference the moves you made and why.",
  "step.reflection_submit": "Submit reflection",
  "step.reflection_submitting": "Submitting…",
  "step.reflection_too_short": "Please write at least a few sentences explaining your reasoning",

  // principle banner
  "banner.principle": "Licensing threshold: no capability claim without observable behaviour. Every action you take is captured and scored against the concept's behavioural rules.",
  "banner.principle_label": "Licensing threshold:",

  // event ticker
  "ticker.label": "[BEHAVIOUR-LOG]",
  "ticker.empty": "No events yet — your actions will appear here.",
  "ticker.events": "{n} events",

  // V2 primitive · iddl_ai_chat (AI-IN-02)
  "sim.ai_chat.workspace_title": "IDDL AI Workspace",
  "sim.ai_chat.submissions": "submissions: {n}",
  "sim.ai_chat.empty_history": "Compose a prompt below to start the conversation. The AI will respond once you press Submit to AI.",
  "sim.ai_chat.role_you": "YOU",
  "sim.ai_chat.role_you_refined": "YOU · REFINED",
  "sim.ai_chat.role_ai_weak": "AI · WEAK RESPONSE",
  "sim.ai_chat.role_ai_strong": "AI · STRONG RESPONSE",
  "sim.ai_chat.inspect_output": "Inspect output",
  "sim.ai_chat.review_weak_title": "Output review · weak",
  "sim.ai_chat.review_weak_b1": "No length constraint — runs on for a paragraph instead of a fixed bullet count.",
  "sim.ai_chat.review_weak_b2": "No audience framing — generic register, not aimed at executives.",
  "sim.ai_chat.review_weak_b3": "No structure — facts and risks are not separated.",
  "sim.ai_chat.review_strong_title": "Output review · strong",
  "sim.ai_chat.review_strong_b1": "Length constraint respected (5 bullets).",
  "sim.ai_chat.review_strong_b2": "Audience framing present (executives).",
  "sim.ai_chat.review_strong_b3": "Structure: facts → risks → action items.",
  "sim.ai_chat.placeholder_first": "Compose your first prompt — what should the AI do with the source material?",
  "sim.ai_chat.placeholder_refine": "Refine your prompt — add a length, audience, or format constraint to close the gap.",
  "sim.ai_chat.btn_submit_first": "Submit to AI",
  "sim.ai_chat.btn_submit_refine": "Submit refinement",
  "sim.ai_chat.hint_after_refinement": "Refinement accepted — output now meets the brief.",
  "sim.ai_chat.hint_weak_output": "Output is weak — refine your prompt with concrete constraints.",
  "sim.ai_chat.source_title": "Source material",
  "sim.ai_chat.final_decision": "Final decision",
  "sim.ai_chat.btn_submit_refined": "Submit refined prompt as final answer",
  "sim.ai_chat.btn_accept_weak": "Accept first output without refining (unsafe)",
  "sim.ai_chat.finalized": "Final decision logged: {decision}.",
  "sim.ai_chat.refine_before_submitting": "The first output is weak. Refine your prompt with a length / audience / format constraint before submitting.",
  "sim.ai_chat.event_failed": "Event {name} failed: {detail}",

  // V2 primitive · iddl_inbox (CS-TA-03)
  "sim.inbox.folder_inbox": "Inbox ({n})",
  "sim.inbox.select_a_message": "Select a message to inspect it.",
  "sim.inbox.inspect_sender": "Inspect sender",
  "sim.inbox.inspect_headers": "Inspect headers",
  "sim.inbox.email_label": "Email:",
  "sim.inbox.real_domain_label": "Real domain:",
  "sim.inbox.headers_red_flags": "Headers · red flags:",
  "sim.inbox.no_anomalies": "No anomalies detected",
  "sim.inbox.links_in_message": "Links in this message",
  "sim.inbox.links_help": "Hover OR click the eye icon to preview the real destination before clicking the link.",
  "sim.inbox.preview_url": "Preview URL",
  "sim.inbox.attachments": "Attachments",
  "sim.inbox.open": "Open",
  "sim.inbox.save": "Save",
  "sim.inbox.report": "Report",
  "sim.inbox.archive": "Archive",
  "sim.inbox.delete": "Delete",
  "sim.inbox.reply": "Reply",
  "sim.inbox.forward": "Forward",
  "sim.inbox.composing": "Composing {kind} to {to}",
  "sim.inbox.compose_placeholder": "Write your message…",
  "sim.inbox.btn_send_unsafe": "Send (unsafe)",
  "sim.inbox.btn_discard": "Discard",
  "sim.inbox.send_warning": "Sending a reply/forward to a suspicious sender is an unsafe action and may auto-fail this concept.",
  "sim.inbox.final_decision": "Final decision",
  "sim.inbox.fd_report_phishing": "Report as phishing",
  "sim.inbox.fd_delete": "Delete only",
  "sim.inbox.fd_ignore": "Ignore (leave it)",
  "sim.inbox.fd_reply": "Reply to sender",
  "sim.inbox.finalized": "Final decision logged: {decision}. You can keep interacting and the most recent final decision is the one scored.",
  // Iter-25 — holistic inbox triage gate
  "sim.inbox.complete_triage_btn": "Complete inbox triage",
  "sim.inbox.continue_to_reflection": "Continue to reflection",
  "sim.inbox.triaged_label": "Inbox triage complete",
  "sim.inbox.triaged_toast": "Inbox triage submitted. Your per-email actions have been recorded.",
  "sim.inbox.triage_progress": "Triaged {acted} of {total} emails",
  "sim.inbox.triage_pending_hint": "{pending} email(s) still need a decision before you can continue",
  "sim.inbox.row_acted": "Actioned",
  "sim.inbox.row_actioned": "Actioned",
  "sim.inbox.row_pending": "PENDING",
  // Iter-25.2 — banner + per-row action labels
  "sim.inbox.banner_instruction": "Take an action on every email before continuing — Report it if it looks suspicious, Archive it if it is safe to keep, Delete it if it is unwanted, or Reply / Forward if it needs engagement.",
  "sim.inbox.row_reported": "REPORTED",
  "sim.inbox.row_archived": "ARCHIVED",
  "sim.inbox.row_deleted": "DELETED",
  "sim.inbox.row_replied": "REPLIED",
  "sim.inbox.row_forwarded": "FORWARDED",
  "sim.inbox.reported_toast": "Reported to security — message moved out of inbox",

  // V2 primitive · iddl_browser (DS-IN-01)
  "sim.browser.research_question": "Research question",
  "sim.browser.search_placeholder": "Type a search query…",
  "sim.browser.btn_search": "Search",
  "sim.browser.btn_refine": "Refine query",
  "sim.browser.btn_back": "Back",
  "sim.browser.btn_inspect_source": "Inspect source",
  "sim.browser.btn_flag_credibility": "Flag credibility",
  "sim.browser.btn_bookmark": "Bookmark",
  "sim.browser.btn_select_answer": "Select as answer",
  "sim.browser.no_results": "No results — try entering a query above.",
  "sim.browser.warn_low_quality": "Selecting a low-quality source here would auto-fail this concept.",
  "sim.browser.final_decision": "Final decision",
  "sim.browser.fd_select_credible": "Select this source as my answer",

  // V2 primitive · iddl_file_manager (CS-TA-04, CS-SB-04)
  "sim.fm.workspace_title": "IDDL Workspace",
  "sim.fm.items": "items",
  "sim.fm.empty_folder": "This folder is empty.",
  "sim.fm.select_a_file": "Select a file from the list to preview, classify, or move it.",
  "sim.fm.preview": "File preview",
  "sim.fm.owner": "Owner",
  "sim.fm.no_content": "(no preview available)",
  "sim.fm.click_inspect": "Press Inspect to reveal the file content.",
  "sim.fm.btn_inspect": "Inspect content",
  "sim.fm.btn_move": "Move…",
  "sim.fm.btn_share": "Share / export…",
  "sim.fm.btn_rename": "Rename",
  "sim.fm.classify_as": "Classify sensitivity",
  "sim.fm.final_decision": "Final decision",
  "sim.fm.btn_submit": "Submit final classifications & placements",
  "sim.fm.finalized": "Submitted: {decision}.",
  "sim.fm.move_to_label": "Move \"{file}\" to which folder?",
  "sim.fm.share_label": "Share or export \"{file}\" — pick a destination",
  "sim.fm.rename_label": "Rename the selected file",
  "sim.fm.save": "Save",
  "sim.fm.cancel": "Cancel",
  "sim.fm.unsafe_action_logged": "Unsafe action logged: \"{file}\" → {destination}. This will fail the concept.",
  "sim.fm.share_completed": "Shared with {destination}.",

  // V2 primitive · iddl_settings (CS-SB-01, CS-SB-03)
  "sim.set.workspace_title": "Device & account settings",
  "sim.set.summary_title": "Configuration health",
  "sim.set.unsafe_count": "{n} unsafe setting(s) in this configuration",
  "sim.set.all_secure": "All settings look secure",
  "sim.set.unsafe_tag": "UNSAFE",
  "sim.set.on": "On",
  "sim.set.off": "Off",
  "sim.set.warning_title": "This value is considered unsafe",
  "sim.set.warning_default": "This setting weakens the security posture of your account or device. You can keep it and acknowledge, or revert it to a safer value before submitting.",
  "sim.set.warning_revert": "Revert to a safe value",
  "sim.set.warning_ack": "I understand — keep it for now",
  "sim.set.btn_submit": "Submit final configuration",
  "sim.set.finalized": "Submitted: {decision}.",

  // V2 primitive · iddl_workflow (CS-IR-01..04)
  "sim.wf.alerts_title": "Inbound alerts",
  "sim.wf.alert_flagged": "FLAGGED · incident",
  "sim.wf.alert_dismissed": "DISMISSED · noise",
  "sim.wf.btn_flag_incident": "Flag as incident",
  "sim.wf.btn_dismiss": "Dismiss · not an incident",
  "sim.wf.plan_title": "Response plan",
  "sim.wf.plan_count": "{n} step(s) in plan",
  "sim.wf.unsafe_count": "{n} unsafe step(s)",
  "sim.wf.palette_title": "Available actions",
  "sim.wf.queue_title": "Your plan (in order)",
  "sim.wf.queue_empty": "Empty plan. Add steps from the palette.",
  "sim.wf.kind_unsafe": "UNSAFE",
  "sim.wf.warning_title": "This step is considered unsafe",
  "sim.wf.warning_default": "This action weakens your response. You may keep it and acknowledge, or remove it before submitting.",
  "sim.wf.warning_remove": "Remove this step",
  "sim.wf.warning_ack": "I understand — keep it for now",
  "sim.wf.warning_revert_channel": "Switch to the safe channel",
  "sim.wf.channel_warning_default": "Reporting via a public or unauthorised channel discloses the incident to the world. Switch to the internal SOC channel.",
  "sim.wf.escalate_title": "Escalation channel",
  "sim.wf.safe_state_summary": "Final plan looks defensible",
  "sim.wf.unsafe_state_summary": "Plan still contains unsafe step(s) or an unsafe channel",
  "sim.wf.btn_submit": "Submit final plan",
  "sim.wf.finalized": "Submitted: {decision}.",

  // V2 primitive · iddl_editor (DS-CM-01, DS-CM-04)
  "sim.ed.original_title": "Original message",
  "sim.ed.composer_title": "Compose",
  "sim.ed.signals_title": "Tone signals",
  "sim.ed.signals_none": "No tone signals defined for this scenario.",
  "sim.ed.from": "From",
  "sim.ed.to": "To",
  "sim.ed.cc": "Cc",
  "sim.ed.subject": "Subject",
  "sim.ed.body_placeholder": "Write your message here…",
  "sim.ed.warning_default": "This phrase is considered unsafe in this context. You can edit it out or acknowledge the trade-off before sending.",
  "sim.ed.warning_ack": "I understand — keep it for now",
  "sim.ed.unsatisfied_count": "{n} unresolved tone signal(s)",
  "sim.ed.draft_ready": "Draft looks defensible",
  "sim.ed.btn_send": "Send message",
  "sim.ed.finalized": "Sent: {decision}.",

  // result
  "result.badge": "Final result · disclosed",
  "result.title": "Your IDDL outcome",
  "result.exit": "Exit to home",
  "result.license_issued": "Licence issued",
  "result.license_body": "A 2-year IDDL Level 1 licence has been issued in your name.",
  "result.no_license": "No licence issued",
  "result.no_license_body": "A licence is only granted on Pass or Merit. You can still download the official record of this attempt below.",
  "result.btn_public_verify": "Public verification page",
  "result.btn_download_license": "Download the licence",
  "result.btn_open_license": "Open the licence (new tab)",
  "result.downloading": "Downloading…",
  "result.downloaded": "Licence download started — check your Downloads folder",
  "result.preparing": "Preparing…",
  "result.download_failed": "Download failed",
  "result.breakdown": "Per-concept breakdown",
  "result.breakdown_sub": "Each concept aggregates Knowledge, Capability and Reflection. Capability is scored from observed behaviour.",
  "result.know": "Know",
  "result.perform": "Perform",
  "result.reason": "Reason",
  "result.behavioural_evidence": "Behavioural evidence",
  "result.remediation": "Remediation",
  "result.timestamps_label": "Attempt timeline",
  "result.started_at": "Started",
  "result.completed_at": "Finished",
  "result.duration": "Duration",

  // verify
  "verify.heading_label": "Public verification",
  "verify.heading": "License status",
  "verify.verifying": "Verifying…",
  "verify.valid": "VALID",
  "verify.invalid": "INVALID",
  "verify.not_found": "NOT FOUND",
  "verify.f_candidate": "Candidate",
  "verify.f_outcome": "Outcome",
  "verify.f_issued": "Issued",
  "verify.f_expires": "Expires",
  "verify.download_pdf": "Download certificate (PDF)",
  "verify.footnote": "IDDL licenses observable digital behaviour — not declared knowledge. A valid license confirms the holder demonstrated all three layers (Knowledge, Capability, Reflection) at Level 1, scored from captured behaviour inside the IDDL simulation environment.",

  // admin
  "admin.gate_label": "Admin · token-gated",
  "admin.gate_title": "Sign in to administer",
  "admin.token_label": "X-Admin-Token",
  "admin.token_ph": "Paste your admin token",
  "admin.username_label": "Username",
  "admin.username_ph": "admin",
  "admin.password_label": "Password",
  "admin.password_ph": "Your password",
  "admin.invalid_credentials": "Invalid username or password",
  "admin.signing_in": "Signing in…",
  "admin.use_token_instead": "Use admin token instead",
  "admin.use_credentials_instead": "Use username & password instead",
  "admin.btn_signin": "Enter admin",
  "admin.dev_token_hint": "Default dev token is",
  "admin.list_label": "Admin · operational attempts",
  "admin.list_title": "Operational Attempts",
  "admin.tab_attempts": "Operational Attempts",
  "admin.tab_research": "Research Dataset Export",
  "admin.tab_bank": "Knowledge Bank",
  "admin.tab_contracts": "Assessment Contracts",
  "admin.tab_engine": "Engine Preview",
  "admin.engine_title": "Engine Preview · live simulator",
  "admin.contracts_title": "Assessment Contracts · behavioural tasks, events, scoring rules",
  "admin.research_title": "Research Dataset Export",
  "admin.tab_db": "Database",
  "admin.tab_candidates": "Candidates",
  "admin.tab_licenses": "Licenses",
  "admin.bank_title": "Knowledge Bank · multiple-choice items",
  "admin.download_all_attempts_xlsx": "Download all attempts (Excel)",
  "bank.subtitle": "All 36 concepts and their MCQs. Edit any item, add new ones, or delete custom additions. DS-IN-01 is profile-rendered per country and shown read-only.",
  "bank.questions": "questions",
  "bank.profile_locked": "DS-IN-01 is rendered from per-country profiles (UK / Jordan / NZ / UAE / default). Edit the profiles in code to change these — they are intentionally locked here so candidate context never drifts mid-attempt.",
  "bank.edit": "Edit",
  "bank.delete": "Delete",
  "bank.add_question": "Add a new question",
  "bank.question_text": "Question text",
  "bank.options_label": "Options (mark the correct one)",
  "bank.option": "Option",
  "bank.mark_correct": "Mark as correct answer",
  "bank.correct_hint": "Click the radio button next to the option that is the correct answer.",
  "bank.save": "Save",
  "bank.cancel": "Cancel",
  "bank.saved": "Question saved",
  "bank.deleted": "Question deleted",
  "bank.confirm_delete": "Delete this question? This cannot be undone for newly created questions; default questions are soft-deleted and can be restored from the database.",
  "bank.err_q": "Question text is required",
  "bank.err_options": "All four options must be filled in",
  "bank.download_xlsx": "Download all questions (Excel)",
  "admin.refresh": "Refresh",
  "admin.signout": "Sign out",
  "admin.download_model_answers": "Download model answers (.docx)",
  "admin.download_model_answers_open": "Open in new tab",
  "admin.download_model_answers_busy": "Preparing…",
  "admin.download_model_answers_ok": "Model answers download started — check your Downloads folder",
  "admin.download_model_answers_failed": "Could not download model answers",
  "admin.invalid_token": "Invalid admin token",
  "admin.no_attempts": "No attempts yet.",
  "admin.loading": "Loading…",
  "admin.select_attempt": "Select an attempt on the left.",
  "admin.approval_toggled": "Approval toggled",
  "admin.in_progress": "in progress",
  "admin.completed": "completed",
  "admin.started": "started",
  "admin.step": "step",
  "admin.approved": "APPROVED",
  "admin.btn_approve": "Approve",
  "admin.btn_revoke": "Approved (click to revoke)",
  "admin.btn_delete": "Delete",
  "admin.confirm_delete_attempt": "Delete attempt for {name}? This permanently removes the attempt AND its associated licence. This cannot be undone.",
  "admin.deleted": "Attempt deleted",
  "admin.deleted_with_license": "Attempt and licence deleted",
  "admin.download_attempt_pdf": "Download attempt PDF",
  "admin.download_attempt_pdf_open": "Open in new tab",
  "admin.download_attempt_pdf_ok": "Attempt PDF download started — check your Downloads folder",
  "admin.download_attempt_pdf_failed": "Could not download attempt PDF",
  "admin.download_license_pdf": "Download licence PDF",
  "admin.scoring_title": "Per-concept scoring (behavioural evidence)",
  "admin.scoring_sub": "Each concept is scored from observed actions inside the simulator — not from declarative answers.",
  "admin.event_log": "Behaviour event log",
  "admin.review_title": "Candidate submission vs model answer",
  "admin.review_q": "Knowledge · Q{n}",
  "admin.review_model": "MODEL",
  "admin.review_candidate": "CANDIDATE",
  "admin.review_correct": "CANDIDATE · CORRECT",
  "admin.cap_expected": "Capability · expected behaviour",
  "admin.cap_valid_finals": "Valid final decisions:",
  "admin.cap_candidate_final": "Candidate final decision:",
  "admin.cap_critical": "Critical behaviours:",
  "admin.cap_missing_critical": "Missing critical:",
  "admin.cap_auto_fail": "Auto-fail triggers:",
  "admin.cap_candidate_triggered": "Candidate triggered:",
  "admin.refl_themes": "Reflection · required themes",
  "admin.refl_synonyms": "synonyms accepted:",
  "admin.refl_candidate_text": "Candidate's reflection",
  "admin.pending": "PENDING",

  // common
  "common.loading": "Loading…",
};

const AR = {
  // header
  "header.brand_name": "IDDL",
  "header.brand_sub": "الرخصة الدولية للقيادة الرقمية",
  "header.switch_lang": "English",
  "header.home": "الرئيسية",

  // landing
  "landing.badge": "IDDL · المستوى الأول · هيكل المعرفة · النسخة 1",
  "landing.brand_full": "الرخصة الدولية للقيادة الرقمية",
  "landing.domain_ds": "المهارات الرقمية",
  "landing.domain_cs": "الوعي بالأمن السيبراني",
  "landing.domain_ai": "كفاءة الذكاء الاصطناعي",
  "landing.tagline": "قدرة رقمية. سلوك موثوق. ترخيص لإحداث الأثر.",
  "landing.btn_candidate": "المتقدم للرخصة",
  "landing.btn_candidate_sub": "ابدأ التقييم",
  "landing.btn_admin": "المشرف",
  "landing.btn_admin_sub": "مراجعة المحاولات",
  "landing.pillar_inspire": "إلهام",
  "landing.pillar_develop": "تطوير",
  "landing.pillar_discover": "اكتشاف",
  "landing.pillar_lead": "قيادة",
  "landing.foot_know": "معرفة — الفهم",
  "landing.foot_perform": "أداء — القدرة السلوكية",
  "landing.foot_reason": "تفكير — التأمل",
  "landing.foot_sequence": "3 مفاهيم · DS ← CS ← AI · تسلسل مقفل",
  "landing.foot_result": "النجاح / الامتياز / الإخفاق يُكشف فقط في النهاية",
  "landing.foot_license": "رخصة لسنتين · تحقق عام",

  // candidate start
  "start.step": "الخطوة 0 · تسجيل مفتوح",
  "start.title": "ابدأ محاولتك في IDDL",
  "start.description": "سيتم تقييمك على ثلاثة مفاهيم تُختار عشوائيًا — واحدٌ من كل من المهارات الرقمية والأمن السيبراني وكفاءة الذكاء الاصطناعي — بهذا الترتيب. لا يمكن تخطّي التسلسل. تُكشف النتيجة فقط بعد إنهاء المفاهيم الثلاثة.",
  "start.label_name": "الاسم الكامل",
  "start.ph_name": "كما يجب أن يظهر في رخصتك",
  "start.label_email": "البريد الإلكتروني · اختياري",
  "start.ph_email": "you@example.org",
  "start.label_country": "الدولة",
  "start.ph_country": "مثال: الأردن، نيوزيلندا، المملكة المتحدة",
  "start.label_invite": "رمز الدعوة · اختياري",
  "start.ph_invite": "اتركه فارغًا إن لم يكن لديك",
  "start.label_photo": "صورة المتقدم للرخصة",
  "start.upload_photo": "ارفع الصورة",
  "start.remove": "إزالة",
  "start.fee_label": "رسوم الامتحان",
  "start.fee_note": "رسوم امتحان IDDL المستوى الأول لمرّة واحدة. الدفع مطلوب لفتح المحاولة.",
  "start.pay": "ادفع {amount} دولارًا أمريكيًا",
  "start.paid": "مدفوع · {amount} دولارًا أمريكيًا",
  "start.btn_start": "ابدأ التقييم",
  "start.btn_start_disabled": "أكمل الحقول المطلوبة والدفع",
  "start.required": "مطلوب: الاسم، الدولة، الصورة، الدفع.",
  "start.photo_missing": "الصورة ناقصة.",
  "start.country_missing": "الدولة ناقصة.",
  "start.payment_pending": "الدفع معلّق.",
  "start.err_name": "الاسم مطلوب",
  "start.err_country": "الدولة مطلوبة",
  "start.err_photo": "الرجاء رفع صورة",
  "start.err_pay": "الرجاء إكمال الدفع للمتابعة",
  "start.starting": "جارٍ البدء…",

  // attempt runner
  "runner.attempt": "المحاولة",
  "runner.sequence": "التسلسل",
  "runner.candidate": "المتقدم للرخصة",
  "runner.concept_of": "المفهوم {n} من {total} ·",
  "runner.step_label": "خطوة {step}",
  "runner.could_not_load": "تعذّر تحميل المحاولة",

  // steps — knowledge
  "step.knowledge_title": "أكّد ما تعرفه",
  "step.knowledge_sub": "أجب عن كل سؤال. لكل سؤال إجابة واحدة أفضل.",
  "step.knowledge_layer": "الطبقة 1 · المعرفة — فهم",
  "step.knowledge_submit": "المتابعة إلى محاكاة القدرة",
  "step.knowledge_submitting": "جارٍ الإرسال…",
  "step.knowledge_q": "س{n}",
  "step.knowledge_answer_all": "أجب عن كل سؤال قبل المتابعة",

  // steps — capability
  "step.capability_layer": "الطبقة 2 · القدرة السلوكية (محاكاة)",
  "step.capability_title": "قدرة سلوكية — {concept}",
  "step.capability_sub": "اختبار سلوكي قائم على محاكاة. أفعالك الملحوظة داخل المحاكي هي الدليل — وليس إجابتك حول ما كنت ستفعله.",
  "step.capability_task": "المهمة",
  "step.capability_events": "{n} حدث سلوكي مُلتقط · حيّ",
  "step.capability_end_early": "إنهاء المحاكاة مبكرًا",
  "step.capability_finalize": "إنهاء",

  // steps — reflection
  "step.reflection_layer": "الطبقة 3 · التأمل — تفكير",
  "step.reflection_title": "برّر قراراتك",
  "step.reflection_sub": "تأمّل في الإجراءات التي اتخذتها فعلًا داخل المحاكي. نُقيّم جودة التفكير — لا طوله أو صياغته.",
  "step.reflection_prompt": "السؤال",
  "step.reflection_placeholder": "استعرض منطقك. أشر إلى الخطوات التي قمت بها وأسبابها.",
  "step.reflection_submit": "إرسال التأمل",
  "step.reflection_submitting": "جارٍ الإرسال…",
  "step.reflection_too_short": "الرجاء كتابة بضع جمل على الأقل لشرح منطقك",

  // principle banner
  "banner.principle": "عتبة الترخيص: لا يُمنح ادّعاء قدرة بدون سلوك قابل للملاحظة. يُسجَّل كل فعل تقوم به ويُقَيَّم وفق القواعد السلوكية للمفهوم.",
  "banner.principle_label": "عتبة الترخيص:",

  // event ticker
  "ticker.label": "[سجل-السلوك]",
  "ticker.empty": "لا توجد أحداث بعد — ستظهر أفعالك هنا.",
  "ticker.events": "{n} حدث",

  // V2 primitive · iddl_ai_chat (AI-IN-02)
  "sim.ai_chat.workspace_title": "مساحة عمل IDDL الذكية",
  "sim.ai_chat.submissions": "عدد الإرساليات: {n}",
  "sim.ai_chat.empty_history": "اكتب موجِّهًا (Prompt) أدناه لبدء المحادثة. سيرد المساعد بعد ضغط زر الإرسال.",
  "sim.ai_chat.role_you": "أنت",
  "sim.ai_chat.role_you_refined": "أنت · مُحَسَّن",
  "sim.ai_chat.role_ai_weak": "الذكاء الاصطناعي · ردّ ضعيف",
  "sim.ai_chat.role_ai_strong": "الذكاء الاصطناعي · ردّ قوي",
  "sim.ai_chat.inspect_output": "افحص المخرَج",
  "sim.ai_chat.review_weak_title": "مراجعة المخرَج · ضعيف",
  "sim.ai_chat.review_weak_b1": "لا يوجد قيد على الطول — فقرة مفتوحة بدل عدد نقاط محدد.",
  "sim.ai_chat.review_weak_b2": "لا تأطير للجمهور — أسلوب عام، لا يخاطب الإدارة التنفيذية.",
  "sim.ai_chat.review_weak_b3": "لا يوجد ترتيب — الحقائق والمخاطر غير مفصولة.",
  "sim.ai_chat.review_strong_title": "مراجعة المخرَج · قوي",
  "sim.ai_chat.review_strong_b1": "قيد الطول مُحترَم (5 نقاط).",
  "sim.ai_chat.review_strong_b2": "تأطير الجمهور موجود (إدارة تنفيذية).",
  "sim.ai_chat.review_strong_b3": "البنية: حقائق ← مخاطر ← خطوات تالية.",
  "sim.ai_chat.placeholder_first": "اكتب موجِّهك الأول — ما الذي تريد أن يفعله المساعد بمواد المصدر؟",
  "sim.ai_chat.placeholder_refine": "حسّن موجِّهك — أضف قيدًا على الطول أو الجمهور أو التنسيق لسد الفجوة.",
  "sim.ai_chat.btn_submit_first": "إرسال إلى الذكاء الاصطناعي",
  "sim.ai_chat.btn_submit_refine": "إرسال التحسين",
  "sim.ai_chat.hint_after_refinement": "تم قبول التحسين — أصبح المخرَج يحقق المطلوب.",
  "sim.ai_chat.hint_weak_output": "المخرَج ضعيف — حسّن موجِّهك بإضافة قيود واضحة.",
  "sim.ai_chat.source_title": "مادة المصدر",
  "sim.ai_chat.final_decision": "القرار النهائي",
  "sim.ai_chat.btn_submit_refined": "اعتماد الموجِّه المحسّن كإجابة نهائية",
  "sim.ai_chat.btn_accept_weak": "قبول المخرَج الأول دون تحسين (غير آمن)",
  "sim.ai_chat.finalized": "تم تسجيل القرار النهائي: {decision}.",
  "sim.ai_chat.refine_before_submitting": "المخرَج الأول ضعيف. حسّن موجِّهك بإضافة قيد على الطول أو الجمهور أو التنسيق قبل الإرسال.",
  "sim.ai_chat.event_failed": "فشل الحدث {name}: {detail}",

  // V2 primitive · iddl_inbox (CS-TA-03)
  "sim.inbox.folder_inbox": "البريد الوارد ({n})",
  "sim.inbox.select_a_message": "اختر رسالة لفحصها.",
  "sim.inbox.inspect_sender": "افحص المُرسِل",
  "sim.inbox.inspect_headers": "افحص ترويسات الرسالة",
  "sim.inbox.email_label": "البريد:",
  "sim.inbox.real_domain_label": "النطاق الحقيقي:",
  "sim.inbox.headers_red_flags": "الترويسات · إشارات تحذيرية:",
  "sim.inbox.no_anomalies": "لا توجد إشارات شاذة",
  "sim.inbox.links_in_message": "الروابط في هذه الرسالة",
  "sim.inbox.links_help": "مرر الفأرة أو اضغط على أيقونة العين لمعاينة الوجهة الحقيقية قبل النقر على الرابط.",
  "sim.inbox.preview_url": "معاينة الرابط",
  "sim.inbox.attachments": "المرفقات",
  "sim.inbox.open": "فتح",
  "sim.inbox.save": "حفظ",
  "sim.inbox.report": "إبلاغ",
  "sim.inbox.archive": "أرشفة",
  "sim.inbox.delete": "حذف",
  "sim.inbox.reply": "رد",
  "sim.inbox.forward": "إعادة توجيه",
  "sim.inbox.composing": "تكتب {kind} إلى {to}",
  "sim.inbox.compose_placeholder": "اكتب رسالتك…",
  "sim.inbox.btn_send_unsafe": "إرسال (غير آمن)",
  "sim.inbox.btn_discard": "إلغاء",
  "sim.inbox.send_warning": "إرسال رد أو إعادة توجيه إلى مُرسِل مشبوه إجراء غير آمن وقد يؤدي إلى رسوب فوري في هذا المفهوم.",
  "sim.inbox.final_decision": "القرار النهائي",
  "sim.inbox.fd_report_phishing": "الإبلاغ كرسالة تصيد",
  "sim.inbox.fd_delete": "الحذف فقط",
  "sim.inbox.fd_ignore": "تجاهل (اتركها)",
  "sim.inbox.fd_reply": "الرد على المُرسِل",
  "sim.inbox.finalized": "تم تسجيل القرار النهائي: {decision}. يمكنك متابعة التفاعل، وأحدث قرار نهائي هو ما يُقَيَّم.",
  // Iter-25 — holistic inbox triage gate
  "sim.inbox.complete_triage_btn": "إكمال فرز البريد",
  "sim.inbox.continue_to_reflection": "المتابعة إلى التأمل",
  "sim.inbox.triaged_label": "تم إكمال فرز البريد",
  "sim.inbox.triaged_toast": "تم إرسال فرز البريد. تم تسجيل إجراءاتك على كل رسالة.",
  "sim.inbox.triage_progress": "تم التعامل مع {acted} من {total} رسالة",
  "sim.inbox.triage_pending_hint": "{pending} رسالة (رسائل) لا تزال بحاجة إلى قرار قبل المتابعة",
  "sim.inbox.row_acted": "تم التعامل",
  "sim.inbox.row_actioned": "تم التعامل",
  "sim.inbox.row_pending": "قيد الانتظار",
  // Iter-25.2 — banner + per-row action labels
  "sim.inbox.banner_instruction": "اتخذ إجراءً لكل رسالة قبل المتابعة — أبلِغ إذا بدت مشبوهة، أرشف إذا كانت آمنة، احذف إذا لم تكن مرغوبة، أو رُد / حوِّل إذا تطلبت تفاعلًا.",
  "sim.inbox.row_reported": "تم الإبلاغ",
  "sim.inbox.row_archived": "تمت الأرشفة",
  "sim.inbox.row_deleted": "تم الحذف",
  "sim.inbox.row_replied": "تم الرد",
  "sim.inbox.row_forwarded": "تمت الإعادة",
  "sim.inbox.reported_toast": "تم الإبلاغ للأمن — نُقلت الرسالة خارج البريد الوارد",

  // V2 primitive · iddl_browser (DS-IN-01)
  "sim.browser.research_question": "سؤال البحث",
  "sim.browser.search_placeholder": "اكتب استفسارك في البحث…",
  "sim.browser.btn_search": "بحث",
  "sim.browser.btn_refine": "تحسين الاستفسار",
  "sim.browser.btn_back": "رجوع",
  "sim.browser.btn_inspect_source": "افحص المصدر",
  "sim.browser.btn_flag_credibility": "أبلغ عن المصداقية",
  "sim.browser.btn_bookmark": "حفظ مرجعي",
  "sim.browser.btn_select_answer": "اختر كإجابة",
  "sim.browser.no_results": "لا توجد نتائج — اكتب استفسارًا في الأعلى.",
  "sim.browser.warn_low_quality": "اختيار مصدر ضعيف هنا يؤدي إلى رسوب فوري في هذا المفهوم.",
  "sim.browser.final_decision": "القرار النهائي",
  "sim.browser.fd_select_credible": "اختر هذا المصدر كإجابتي",

  // V2 primitive · iddl_file_manager (CS-TA-04, CS-SB-04)
  "sim.fm.workspace_title": "مساحة عمل IDDL",
  "sim.fm.items": "عناصر",
  "sim.fm.empty_folder": "هذا المجلد فارغ.",
  "sim.fm.select_a_file": "اختر ملفًا من القائمة لمعاينته أو تصنيفه أو نقله.",
  "sim.fm.preview": "معاينة الملف",
  "sim.fm.owner": "المالك",
  "sim.fm.no_content": "(لا تتوفر معاينة)",
  "sim.fm.click_inspect": "اضغط فحص لكشف محتوى الملف.",
  "sim.fm.btn_inspect": "افحص المحتوى",
  "sim.fm.btn_move": "نقل…",
  "sim.fm.btn_share": "مشاركة / تصدير…",
  "sim.fm.btn_rename": "إعادة تسمية",
  "sim.fm.classify_as": "صنّف الحساسية",
  "sim.fm.final_decision": "القرار النهائي",
  "sim.fm.btn_submit": "أرسل التصنيفات والمواقع النهائية",
  "sim.fm.finalized": "تم الإرسال: {decision}.",
  "sim.fm.move_to_label": "إلى أي مجلد تريد نقل \"{file}\"؟",
  "sim.fm.share_label": "شارك أو صدّر \"{file}\" — اختر الوجهة",
  "sim.fm.rename_label": "أعد تسمية الملف المحدد",
  "sim.fm.save": "حفظ",
  "sim.fm.cancel": "إلغاء",
  "sim.fm.unsafe_action_logged": "تم تسجيل إجراء غير آمن: \"{file}\" → {destination}. سيؤدي هذا إلى رسوب في هذا المفهوم.",
  "sim.fm.share_completed": "تمت المشاركة مع {destination}.",

  // V2 primitive · iddl_settings (CS-SB-01, CS-SB-03)
  "sim.set.workspace_title": "إعدادات الجهاز والحساب",
  "sim.set.summary_title": "حالة الإعدادات",
  "sim.set.unsafe_count": "{n} إعداد غير آمن في هذا التكوين",
  "sim.set.all_secure": "جميع الإعدادات تبدو آمنة",
  "sim.set.unsafe_tag": "غير آمن",
  "sim.set.on": "مفعّل",
  "sim.set.off": "معطّل",
  "sim.set.warning_title": "هذه القيمة تُعدّ غير آمنة",
  "sim.set.warning_default": "يُضعف هذا الإعداد الوضع الأمني لحسابك أو جهازك. يمكنك إبقاءه والإقرار بالمخاطر، أو إعادته إلى قيمة أكثر أمانًا قبل الإرسال.",
  "sim.set.warning_revert": "العودة إلى قيمة آمنة",
  "sim.set.warning_ack": "أفهم — أبقِه في الوقت الحالي",
  "sim.set.btn_submit": "أرسل التكوين النهائي",
  "sim.set.finalized": "تم الإرسال: {decision}.",

  // V2 primitive · iddl_workflow (CS-IR-01..04)
  "sim.wf.alerts_title": "التنبيهات الواردة",
  "sim.wf.alert_flagged": "مُعلَّم · حادث",
  "sim.wf.alert_dismissed": "مُستبعد · ضوضاء",
  "sim.wf.btn_flag_incident": "حدِّد كحادث",
  "sim.wf.btn_dismiss": "استبعد · ليس حادثًا",
  "sim.wf.plan_title": "خطة الاستجابة",
  "sim.wf.plan_count": "{n} خطوة في الخطة",
  "sim.wf.unsafe_count": "{n} خطوة غير آمنة",
  "sim.wf.palette_title": "الإجراءات المتاحة",
  "sim.wf.queue_title": "خطتك (بالترتيب)",
  "sim.wf.queue_empty": "الخطة فارغة. أضف خطوات من لوحة الإجراءات.",
  "sim.wf.kind_unsafe": "غير آمن",
  "sim.wf.warning_title": "تُعدّ هذه الخطوة غير آمنة",
  "sim.wf.warning_default": "يُضعف هذا الإجراء استجابتك. يمكنك إبقاؤه والإقرار بالمخاطر، أو إزالته قبل الإرسال.",
  "sim.wf.warning_remove": "أزل هذه الخطوة",
  "sim.wf.warning_ack": "أفهم — أبقها في الوقت الحالي",
  "sim.wf.warning_revert_channel": "التحول إلى القناة الآمنة",
  "sim.wf.channel_warning_default": "الإبلاغ عبر قناة عامة أو غير مصرح بها يكشف الحادث للعموم. حوّل إلى قناة SOC الداخلية.",
  "sim.wf.escalate_title": "قناة التصعيد",
  "sim.wf.safe_state_summary": "تبدو الخطة النهائية قابلة للدفاع",
  "sim.wf.unsafe_state_summary": "ما زالت الخطة تحتوي على خطوة (خطوات) غير آمنة أو قناة غير آمنة",
  "sim.wf.btn_submit": "أرسل الخطة النهائية",
  "sim.wf.finalized": "تم الإرسال: {decision}.",

  // V2 primitive · iddl_editor (DS-CM-01, DS-CM-04)
  "sim.ed.original_title": "الرسالة الأصلية",
  "sim.ed.composer_title": "إنشاء",
  "sim.ed.signals_title": "إشارات النبرة",
  "sim.ed.signals_none": "لا توجد إشارات نبرة معرَّفة لهذا السيناريو.",
  "sim.ed.from": "من",
  "sim.ed.to": "إلى",
  "sim.ed.cc": "نسخة",
  "sim.ed.subject": "الموضوع",
  "sim.ed.body_placeholder": "اكتب رسالتك هنا…",
  "sim.ed.warning_default": "تُعدّ هذه العبارة غير ملائمة في هذا السياق. يمكنك تعديلها أو الإقرار قبل الإرسال.",
  "sim.ed.warning_ack": "أفهم — أبقها في الوقت الحالي",
  "sim.ed.unsatisfied_count": "{n} إشارة نبرة غير محلولة",
  "sim.ed.draft_ready": "تبدو المسوّدة قابلة للدفاع",
  "sim.ed.btn_send": "أرسل الرسالة",
  "sim.ed.finalized": "تم الإرسال: {decision}.",

  // result
  "result.badge": "النتيجة النهائية · مُعلنة",
  "result.title": "نتيجتك في IDDL",
  "result.exit": "الخروج للرئيسية",
  "result.license_issued": "تم إصدار الرخصة",
  "result.license_body": "تم إصدار رخصة IDDL المستوى الأول باسمك لمدة سنتين.",
  "result.no_license": "لم تُصدر رخصة",
  "result.no_license_body": "تُمنح الرخصة فقط عند النجاح أو الامتياز. يمكنك تنزيل السجل الرسمي للمحاولة أدناه.",
  "result.btn_public_verify": "صفحة التحقق العام",
  "result.btn_download_license": "تنزيل الرخصة",
  "result.btn_open_license": "فتح الرخصة (علامة تبويب جديدة)",
  "result.downloading": "جارٍ التنزيل…",
  "result.downloaded": "بدأ تنزيل الرخصة — تحقّق من مجلد التنزيلات",
  "result.preparing": "جارٍ التحضير…",
  "result.download_failed": "فشل التنزيل",
  "result.breakdown": "التفصيل حسب كل مفهوم",
  "result.breakdown_sub": "كل مفهوم يجمع بين المعرفة والقدرة والتأمل. تُقيَّم القدرة من السلوك الملحوظ.",
  "result.know": "فهم",
  "result.perform": "أداء",
  "result.reason": "تفكير",
  "result.behavioural_evidence": "الأدلة السلوكية",
  "result.remediation": "التوصية",
  "result.timestamps_label": "الجدول الزمني للمحاولة",
  "result.started_at": "بدأت",
  "result.completed_at": "انتهت",
  "result.duration": "المدة",

  // verify
  "verify.heading_label": "التحقق العام",
  "verify.heading": "حالة الرخصة",
  "verify.verifying": "جارٍ التحقق…",
  "verify.valid": "صالحة",
  "verify.invalid": "غير صالحة",
  "verify.not_found": "غير موجودة",
  "verify.f_candidate": "المتقدم للرخصة",
  "verify.f_outcome": "النتيجة",
  "verify.f_issued": "تاريخ الإصدار",
  "verify.f_expires": "تاريخ الانتهاء",
  "verify.download_pdf": "تنزيل الشهادة (PDF)",
  "verify.footnote": "تمنح IDDL الرخصة بناءً على السلوك الرقمي الملحوظ — لا على المعرفة المُعلنة. الرخصة الصالحة تؤكد أن صاحبها أظهر الطبقات الثلاث (المعرفة والقدرة والتأمل) عند المستوى الأول، مُقَيَّمة من السلوك المُلتقط داخل بيئة محاكاة IDDL.",

  // admin
  "admin.gate_label": "المشرف · مُحمي بالرمز",
  "admin.gate_title": "سجّل الدخول للإدارة",
  "admin.token_label": "X-Admin-Token",
  "admin.token_ph": "ألصق رمز المشرف هنا",
  "admin.username_label": "اسم المستخدم",
  "admin.username_ph": "admin",
  "admin.password_label": "كلمة المرور",
  "admin.password_ph": "كلمة المرور",
  "admin.invalid_credentials": "اسم المستخدم أو كلمة المرور غير صحيحة",
  "admin.signing_in": "جاري تسجيل الدخول…",
  "admin.use_token_instead": "استخدم رمز المشرف بدلاً من ذلك",
  "admin.use_credentials_instead": "استخدم اسم المستخدم وكلمة المرور",
  "admin.btn_signin": "دخول المشرف",
  "admin.dev_token_hint": "رمز التطوير الافتراضي هو",
  "admin.list_label": "المشرف · المحاولات التشغيلية",
  "admin.list_title": "المحاولات التشغيلية",
  "admin.tab_attempts": "المحاولات التشغيلية",
  "admin.tab_research": "تصدير بيانات البحث",
  "admin.tab_bank": "بنك المعرفة",
  "admin.tab_contracts": "عقود التقييم",
  "admin.tab_engine": "معاينة المحرّك",
  "admin.engine_title": "معاينة المحرّك · المحاكي الحيّ",
  "admin.contracts_title": "عقود التقييم · المهام السلوكية والأحداث وقواعد التقييم",
  "admin.research_title": "تصدير بيانات البحث",
  "admin.tab_db": "قاعدة البيانات",
  "admin.tab_candidates": "المرشحون",
  "admin.tab_licenses": "الرخص",
  "admin.bank_title": "بنك المعرفة · أسئلة الاختيار من متعدد",
  "admin.download_all_attempts_xlsx": "تنزيل كل المحاولات (Excel)",
  "bank.subtitle": "جميع الـ 36 مفهومًا وأسئلتها. عدِّل أي عنصر، أضف جديدًا، أو احذف الإضافات المخصصة. DS-IN-01 يُعرض حسب الدولة وللقراءة فقط.",
  "bank.questions": "سؤال",
  "bank.profile_locked": "يُرسم DS-IN-01 من ملفات الدولة (المملكة المتحدة / الأردن / نيوزيلندا / الإمارات / الافتراضي). عدِّل الملفات في الشيفرة لتغيير هذه الأسئلة — مقفلة هنا حتى لا يختلف سياق المتقدم أثناء المحاولة.",
  "bank.edit": "تعديل",
  "bank.delete": "حذف",
  "bank.add_question": "أضف سؤالاً جديدًا",
  "bank.question_text": "نص السؤال",
  "bank.options_label": "الخيارات (حدّد الإجابة الصحيحة)",
  "bank.option": "خيار",
  "bank.mark_correct": "حدّد كإجابة صحيحة",
  "bank.correct_hint": "اضغط زر الاختيار بجانب الخيار الصحيح.",
  "bank.save": "حفظ",
  "bank.cancel": "إلغاء",
  "bank.saved": "تم حفظ السؤال",
  "bank.deleted": "تم حذف السؤال",
  "bank.confirm_delete": "هل تريد حذف هذا السؤال؟ لا يمكن التراجع عن حذف الأسئلة المضافة حديثًا.",
  "bank.err_q": "نص السؤال مطلوب",
  "bank.err_options": "يجب تعبئة الخيارات الأربعة",
  "bank.download_xlsx": "تنزيل كل الأسئلة (Excel)",
  "admin.refresh": "تحديث",
  "admin.signout": "تسجيل الخروج",
  "admin.download_model_answers": "تنزيل الإجابات النموذجية (.docx)",
  "admin.download_model_answers_open": "فتح في علامة تبويب جديدة",
  "admin.download_model_answers_busy": "جارٍ التحضير…",
  "admin.download_model_answers_ok": "بدأ تنزيل الإجابات النموذجية — تحقّق من مجلد التنزيلات",
  "admin.download_model_answers_failed": "تعذّر تنزيل الإجابات النموذجية",
  "admin.invalid_token": "رمز المشرف غير صحيح",
  "admin.no_attempts": "لا توجد محاولات بعد.",
  "admin.loading": "جارٍ التحميل…",
  "admin.select_attempt": "اختر محاولة من القائمة على اليمين.",
  "admin.approval_toggled": "تم تبديل الموافقة",
  "admin.in_progress": "قيد التنفيذ",
  "admin.completed": "اكتملت",
  "admin.started": "بدأت",
  "admin.step": "خطوة",
  "admin.approved": "معتمد",
  "admin.btn_approve": "اعتمد",
  "admin.btn_revoke": "معتمد (اضغط للإلغاء)",
  "admin.btn_delete": "حذف",
  "admin.confirm_delete_attempt": "حذف محاولة {name}؟ سيؤدي ذلك إلى حذف المحاولة والرخصة المرتبطة بها نهائيًا ولا يمكن التراجع.",
  "admin.deleted": "تم حذف المحاولة",
  "admin.deleted_with_license": "تم حذف المحاولة والرخصة",
  "admin.download_attempt_pdf": "تنزيل ملف المحاولة PDF",
  "admin.download_attempt_pdf_open": "فتح في علامة تبويب جديدة",
  "admin.download_attempt_pdf_ok": "بدأ تنزيل ملف المحاولة — تحقّق من مجلد التنزيلات",
  "admin.download_attempt_pdf_failed": "تعذّر تنزيل ملف المحاولة",
  "admin.download_license_pdf": "تنزيل ملف الرخصة PDF",
  "admin.scoring_title": "تقييم كل مفهوم (دليل سلوكي)",
  "admin.scoring_sub": "يُقيَّم كل مفهوم من الأفعال الملحوظة داخل المحاكي — لا من الإجابات المُعلنة.",
  "admin.event_log": "سجل الأحداث السلوكية",
  "admin.review_title": "إجابة المتقدم مقابل الإجابة النموذجية",
  "admin.review_q": "المعرفة · س{n}",
  "admin.review_model": "نموذج",
  "admin.review_candidate": "المتقدم",
  "admin.review_correct": "المتقدم · صحيحة",
  "admin.cap_expected": "القدرة · السلوك المتوقَّع",
  "admin.cap_valid_finals": "القرارات النهائية الصحيحة:",
  "admin.cap_candidate_final": "قرار المتقدم النهائي:",
  "admin.cap_critical": "السلوكيات الحرجة:",
  "admin.cap_missing_critical": "حرج مفقود:",
  "admin.cap_auto_fail": "محفّزات الإخفاق التلقائي:",
  "admin.cap_candidate_triggered": "ما حفّزه المتقدم:",
  "admin.refl_themes": "التأمل · المحاور المطلوبة",
  "admin.refl_synonyms": "المرادفات المقبولة:",
  "admin.refl_candidate_text": "تأمل المتقدم",
  "admin.pending": "معلّق",

  // common
  "common.loading": "جارٍ التحميل…",
};

const MI = {
  // header
  "header.brand_name": "IDDL",
  "header.brand_sub": "Raihana Tuakiri Matihiko o te Ao",
  "header.switch_lang": "Māori",
  "header.home": "Kāinga",

  // landing
  "landing.badge": "IDDL · TAUMATA TUATAHI · TINANA MŌHIOTANGA V1",
  "landing.brand_full": "Raihana Tuakiri Matihiko o te Ao",
  "landing.domain_ds": "Pūkenga Matihiko",
  "landing.domain_cs": "Mōhio Haumarutanga Ipurangi",
  "landing.domain_ai": "Pūkenga Atamai Iahiko",
  "landing.tagline": "Āheinga Matihiko. Whanonga Pono. Raihana mō te Pānga.",
  "landing.btn_candidate": "Kaitono",
  "landing.btn_candidate_sub": "Tīmata aromatawai",
  "landing.btn_admin": "Kaiwhakahaere",
  "landing.btn_admin_sub": "Arotake ngana",
  "landing.pillar_inspire": "WHAKAOHO",
  "landing.pillar_develop": "WHAKAWHANAKE",
  "landing.pillar_discover": "TŪHURA",
  "landing.pillar_lead": "ĀRAHI",
  "landing.foot_know": "Mōhio — mōhiotanga",
  "landing.foot_perform": "Mahi — āheinga whanonga",
  "landing.foot_reason": "Whakaaro — whakaaroaro",
  "landing.foot_sequence": "3 kaupapa · DS → CS → AI · raupapa rāhuitia",
  "landing.foot_result": "Tūmau / Toa / Hapa — ka whakaaturia i te mutunga",
  "landing.foot_license": "Raihana 2 tau · whakaū tūmatanui",

  // candidate start
  "start.step": "Hīkoi 0 · Rēhita tuwhera",
  "start.title": "Tīmatahia tō ngana IDDL",
  "start.description": "Ka aromatawaihia koe i runga i ngā kaupapa e toru kua tohua matapōkere — kotahi mai i ngā Pūkenga Matihiko, te Haumarutanga Ipurangi, me te Atamai Iahiko — i taua raupapa. Kāore e taea te peke. Ka whakaaturia te hua i muri i ngā mea katoa e toru.",
  "start.label_name": "Ingoa katoa",
  "start.ph_name": "Pēnā e tika ana ki tō raihana",
  "start.label_email": "Īmēra · kōwhiri",
  "start.ph_email": "koe@tauira.org",
  "start.label_country": "Whenua",
  "start.ph_country": "hei tauira: Aotearoa, Hōrana, Te Kīngitanga o Ingarangi",
  "start.label_invite": "Tohu pōhiri · kōwhiri",
  "start.ph_invite": "Waiho noa mēnā kāore",
  "start.label_photo": "Whakaahua kaitono",
  "start.upload_photo": "Tukuatu whakaahua",
  "start.remove": "tangohia",
  "start.fee_label": "Utu whakamātautau",
  "start.fee_note": "Utu kotahi mō te whakamātautau IDDL Taumata Tuatahi. Me utu kia tuwhera ai tō ngana.",
  "start.pay": "Utu US$ {amount}.00",
  "start.paid": "Kua utua · US$ {amount}.00",
  "start.btn_start": "Tīmata aromatawai",
  "start.btn_start_disabled": "Whakaotia ngā āpure me te utu",
  "start.required": "Whakaritea: ingoa, whenua, whakaahua, utu.",
  "start.photo_missing": "Kāore te whakaahua.",
  "start.country_missing": "Kāore te whenua.",
  "start.payment_pending": "Utu e tatari ana.",
  "start.err_name": "Me whakaurua he ingoa",
  "start.err_country": "Me whakaurua he whenua",
  "start.err_photo": "Tukuatu he whakaahua",
  "start.err_pay": "Whakaotia te utu kia haere tonu ai",
  "start.starting": "E tīmata ana…",

  // attempt runner chrome
  "runner.attempt": "Ngana",
  "runner.sequence": "Raupapa",
  "runner.candidate": "Kaitono",
  "runner.concept_of": "Kaupapa {n} o {total} ·",
  "runner.step_label": "Hīkoi {step}",
  "runner.could_not_load": "Kāore i taea te utaina te ngana",

  // steps — knowledge
  "step.knowledge_title": "Whakaūngia ō mōhiotanga",
  "step.knowledge_sub": "Whakautua ia pātai. Kotahi noa te whakautu tika mō ia pātai.",
  "step.knowledge_layer": "Paparanga 1 · Mōhiotanga — Mōhio",
  "step.knowledge_submit": "Haere ki te whakamātautau āheinga",
  "step.knowledge_submitting": "E tuku ana…",
  "step.knowledge_q": "P{n}",
  "step.knowledge_answer_all": "Whakautua ngā pātai katoa i mua i te haere tonu",

  // steps — capability
  "step.capability_layer": "Paparanga 2 · Āheinga whanonga (whakatauira)",
  "step.capability_title": "Āheinga whanonga — {concept}",
  "step.capability_sub": "Whakamātautau whanonga i runga i te whakatauira. Ko ngā mahi ka kitea i roto i te whakatauira te taunakitanga — ehara i tō whakautu mō te aha ka mahi koe.",
  "step.capability_task": "Mahi",
  "step.capability_events": "{n} ngā mahi whanonga kua hopukina · ora",
  "step.capability_end_early": "Whakamutua te whakatauira i mua i te wā",
  "step.capability_finalize": "Whakatutuki",

  // steps — reflection
  "step.reflection_layer": "Paparanga 3 · Whakaaroaro — Whakaaro",
  "step.reflection_title": "Whakaūngia ō whakatau",
  "step.reflection_sub": "Whakaarohia ngā mahi i mahia e koe i roto i te whakatauira. Ka aromatawaihia te kounga o te whakaaroaro — kaua te roa, kaua te whakawehenga kupu.",
  "step.reflection_prompt": "Whakatakanga",
  "step.reflection_placeholder": "Whakamāramahia tō whakaaroaro. Whakahuatia ngā nekehanga i mahia e koe me te take.",
  "step.reflection_submit": "Tukuna te whakaaroaro",
  "step.reflection_submitting": "E tuku ana…",
  "step.reflection_too_short": "Tuhia ētahi rerenga e whakamārama ana i tō whakaaroaro",

  // principle banner
  "banner.principle": "Paerewa raihana: kāore he kerēme āheinga me kore he whanonga ka kitea. Ka hopukina ia mahi, ka aromatawaihia ki ngā ture whanonga o te kaupapa.",
  "banner.principle_label": "Paerewa raihana:",

  // event ticker
  "ticker.label": "[RĀRANGI-WHANONGA]",
  "ticker.empty": "Kāore anō he mahi — ka puta ō nekehanga ki konei.",
  "ticker.events": "{n} ngā mahi",

  // V2 primitive · iddl_ai_chat (AI-IN-02)
  "sim.ai_chat.workspace_title": "Wāhi Mahi Atamai IDDL",
  "sim.ai_chat.submissions": "tukutuku: {n}",
  "sim.ai_chat.empty_history": "Tuhia he kōrero whakatakanga ki raro hei tīmata i te kōrerorero. Ka whakautu te AI ina pēhia te pātene Tuku.",
  "sim.ai_chat.role_you": "KOE",
  "sim.ai_chat.role_you_refined": "KOE · KUA WHAKAPAIA",
  "sim.ai_chat.role_ai_weak": "AI · WHAKAUTU NGOIKORE",
  "sim.ai_chat.role_ai_strong": "AI · WHAKAUTU KAHA",
  "sim.ai_chat.inspect_output": "Tirohia te putanga",
  "sim.ai_chat.review_weak_title": "Arotake putanga · ngoikore",
  "sim.ai_chat.review_weak_b1": "Kāore he here roa — kotahi anake te kōwae, kāore he tatauranga ira motuhake.",
  "sim.ai_chat.review_weak_b2": "Kāore he tāpaetanga hunga — kupu noa, ehara mō ngā kaiwhakahaere.",
  "sim.ai_chat.review_weak_b3": "Kāore he hanganga — kāore i wehea ngā meka me ngā mōrearea.",
  "sim.ai_chat.review_strong_title": "Arotake putanga · kaha",
  "sim.ai_chat.review_strong_b1": "Kua whakaūngia te here roa (5 ngā ira).",
  "sim.ai_chat.review_strong_b2": "Kei te whai te tāpaetanga hunga (kaiwhakahaere).",
  "sim.ai_chat.review_strong_b3": "Hanganga: meka → mōrearea → mahi.",
  "sim.ai_chat.placeholder_first": "Tuhia tō kōrero whakatakanga tuatahi — he aha hei mahi ma te AI?",
  "sim.ai_chat.placeholder_refine": "Whakapaia tō kōrero whakatakanga — tāpirihia he here roa, hunga, hōputu rānei.",
  "sim.ai_chat.btn_submit_first": "Tuku ki te AI",
  "sim.ai_chat.btn_submit_refine": "Tuku whakapaitanga",
  "sim.ai_chat.hint_after_refinement": "Kua manakohia te whakapaitanga — kua tutuki te putanga i te whāinga.",
  "sim.ai_chat.hint_weak_output": "He ngoikore te putanga — whakapaia tō kōrero whakatakanga ki ngā here mārama.",
  "sim.ai_chat.source_title": "Ngā taunakitanga puna",
  "sim.ai_chat.final_decision": "Whakatau whakamutunga",
  "sim.ai_chat.btn_submit_refined": "Tukuna te kōrero kua whakapaia hei whakautu whakamutunga",
  "sim.ai_chat.btn_accept_weak": "Whakaae ki te putanga tuatahi me te kore whakapaitanga (kāore i te haumaru)",
  "sim.ai_chat.finalized": "Kua rēhitatia te whakatau whakamutunga: {decision}.",
  "sim.ai_chat.refine_before_submitting": "He ngoikore te putanga tuatahi. Whakapaia tō kōrero ki te tāpiri here roa / hunga / hōputu i mua i te tuku.",
  "sim.ai_chat.event_failed": "I rahua te mahi {name}: {detail}",

  // V2 primitive · iddl_inbox (CS-TA-03)
  "sim.inbox.folder_inbox": "Pouaka Whakauru ({n})",
  "sim.inbox.select_a_message": "Kōwhiria he karere hei tirotiro.",
  "sim.inbox.inspect_sender": "Tirohia te kaituku",
  "sim.inbox.inspect_headers": "Tirohia ngā poutuhi",
  "sim.inbox.email_label": "Īmēra:",
  "sim.inbox.real_domain_label": "Rohe pono:",
  "sim.inbox.headers_red_flags": "Poutuhi · tohu whakatūpato:",
  "sim.inbox.no_anomalies": "Kāore he tohu rerekē i kitea",
  "sim.inbox.links_in_message": "Ngā hononga i tēnei karere",
  "sim.inbox.links_help": "Whakamahia te kiore, pēhia rānei te karu hei whakaata i te haerenga pono i mua i te pā ki te hononga.",
  "sim.inbox.preview_url": "Whakaata URL",
  "sim.inbox.attachments": "Ngā kōnae āpiti",
  "sim.inbox.open": "Tuwhera",
  "sim.inbox.save": "Tiakina",
  "sim.inbox.report": "Pūrongo",
  "sim.inbox.archive": "Pūranga",
  "sim.inbox.delete": "Mukua",
  "sim.inbox.reply": "Whakautu",
  "sim.inbox.forward": "Tuku tuarua",
  "sim.inbox.composing": "Kei te tuhi {kind} ki {to}",
  "sim.inbox.compose_placeholder": "Tuhia tō karere…",
  "sim.inbox.btn_send_unsafe": "Tuku (kāore i te haumaru)",
  "sim.inbox.btn_discard": "Whakakorehia",
  "sim.inbox.send_warning": "He mahi kāore i te haumaru te tuku whakautu / tuku tuarua ki te kaituku whakapae, ā, ka taea pea te whakatakē motuhake i tēnei kaupapa.",
  "sim.inbox.final_decision": "Whakatau whakamutunga",
  "sim.inbox.fd_report_phishing": "Pūrongo hei phishing",
  "sim.inbox.fd_delete": "Mukua noa",
  "sim.inbox.fd_ignore": "Wareware (waiho)",
  "sim.inbox.fd_reply": "Whakautu ki te kaituku",
  "sim.inbox.finalized": "Kua rēhitatia te whakatau whakamutunga: {decision}. Ka taea tonu te whakawhitiwhiti, ā, ko te whakatau whakamutunga hou rawa te mea ka aromatawaihia.",
  // Iter-25 — holistic inbox triage gate
  "sim.inbox.complete_triage_btn": "Whakaoti te tirotiro pouaka",
  "sim.inbox.continue_to_reflection": "Haere tonu ki te whaiwhakaaro",
  "sim.inbox.triaged_label": "Kua oti te tirotiro pouaka",
  "sim.inbox.triaged_toast": "Kua tukuna te tirotiro pouaka. Kua rēhitatia āu mahi mō ia īmēra.",
  "sim.inbox.triage_progress": "Kua tirohia {acted} o {total} īmēra",
  "sim.inbox.triage_pending_hint": "Kei te tatari {pending} īmēra ki tētahi whakatau i mua i te haere tonu",
  "sim.inbox.row_acted": "Kua mahia",
  "sim.inbox.row_actioned": "Kua mahia",
  "sim.inbox.row_pending": "E TATARI ANA",
  // Iter-25.2 — banner + per-row action labels
  "sim.inbox.banner_instruction": "Mahi i tētahi mahi mō ia īmēra i mua i te haere tonu — Pūrongo mēnā he āhua tūpato, Pūranga mēnā he haumaru, Muku mēnā kāore i hiahiatia, Whakautu / Tukutuku rānei mēnā ka tika.",
  "sim.inbox.row_reported": "KUA PŪRONGOHIA",
  "sim.inbox.row_archived": "KUA PŪRANGAHIA",
  "sim.inbox.row_deleted": "KUA MUKUA",
  "sim.inbox.row_replied": "KUA WHAKAUTUA",
  "sim.inbox.row_forwarded": "KUA TUKUTUKUA",
  "sim.inbox.reported_toast": "Kua pūrongotia ki te haumaru — kua nukuhia te karere i te pouaka whakauru",

  // V2 primitive · iddl_browser (DS-IN-01)
  "sim.browser.research_question": "Pātai rangahau",
  "sim.browser.search_placeholder": "Tuhia he tono rapunga…",
  "sim.browser.btn_search": "Rapua",
  "sim.browser.btn_refine": "Whakapai tono",
  "sim.browser.btn_back": "Hoki",
  "sim.browser.btn_inspect_source": "Tirohia te puna",
  "sim.browser.btn_flag_credibility": "Tohu pono",
  "sim.browser.btn_bookmark": "Tohu wāhi",
  "sim.browser.btn_select_answer": "Kōwhiria hei whakautu",
  "sim.browser.no_results": "Kāore he hua — tuhia he tono ki runga.",
  "sim.browser.warn_low_quality": "Mā te kōwhiri puna kounga iti ka taea te whakatakē motuhake i tēnei kaupapa.",
  "sim.browser.final_decision": "Whakatau whakamutunga",
  "sim.browser.fd_select_credible": "Kōwhiria tēnei puna hei whakautu māku",

  // result
  "result.badge": "Hua whakamutunga · kua whākina",
  "result.title": "Tō hua IDDL",
  "result.exit": "Hoki ki te kāinga",
  "result.license_issued": "Kua tukuna te raihana",
  "result.license_body": "Kua tukuna he raihana IDDL Taumata Tuatahi e 2 tau te roa ki tō ingoa.",
  "result.no_license": "Kāore he raihana i tukuna",
  "result.no_license_body": "Ka tukuna anake he raihana mō te Tūmau, te Toa rānei. Ka taea tonu te tikiake i te tuhinga whaimana o tēnei ngana ki raro.",
  "result.btn_public_verify": "Wharangi whakaū tūmatanui",
  "result.btn_download_license": "Tikiakehia te raihana",
  "result.btn_open_license": "Whakatuwheratia te raihana (tāpā hou)",
  "result.downloading": "E tikiake ana…",
  "result.downloaded": "Kua tīmata te tikiake raihana — tirohia tō kōpaki Tikiake",
  "result.preparing": "E whakareri ana…",
  "result.download_failed": "I rahua te tikiake",
  "result.breakdown": "Whakawehenga ia kaupapa",
  "result.breakdown_sub": "Ka whakahiatohia ia kaupapa i te Mōhiotanga, te Āheinga me te Whakaaroaro. Ka aromatawaihia te Āheinga mai i te whanonga ka kitea.",
  "result.know": "Mōhio",
  "result.perform": "Mahi",
  "result.reason": "Whakaaro",
  "result.behavioural_evidence": "Taunakitanga whanonga",
  "result.remediation": "Whakatikatika",
  "result.timestamps_label": "Wā ngana",
  "result.started_at": "I tīmata",
  "result.completed_at": "I oti",
  "result.duration": "Roanga",

  // verify
  "verify.heading_label": "Whakaū tūmatanui",
  "verify.heading": "Tūnga raihana",
  "verify.verifying": "E whakaū ana…",
  "verify.valid": "WHAIMANA",
  "verify.invalid": "KORE WHAIMANA",
  "verify.not_found": "KĀORE I KITEA",
  "verify.f_candidate": "Kaitono",
  "verify.f_outcome": "Hua",
  "verify.f_issued": "I tukuna",
  "verify.f_expires": "Ka pau",
  "verify.download_pdf": "Tikiakehia te tiwhikete (PDF)",
  "verify.footnote": "Ka raihanatia e IDDL te whanonga matihiko ka kitea — ehara ko te mōhiotanga ka kīia. Ka whakaū te raihana whaimana i te kaipupuri kua whakaatu i ngā paparanga e toru (Mōhiotanga, Āheinga, Whakaaroaro) ki te Taumata Tuatahi, kua aromatawaihia mai i ngā whanonga kua hopukina i roto i te taiao whakatauira IDDL.",

  // admin
  "admin.gate_label": "Kaiwhakahaere · kua tiakina",
  "admin.gate_title": "Takiuru hei whakahaere",
  "admin.token_label": "X-Admin-Token",
  "admin.token_ph": "Pikihia tō tohu kaiwhakahaere",
  "admin.username_label": "Ingoa kaiwhakamahi",
  "admin.username_ph": "admin",
  "admin.password_label": "Kupuhipa",
  "admin.password_ph": "Tō kupuhipa",
  "admin.invalid_credentials": "He ingoa, he kupuhipa rānei hē",
  "admin.signing_in": "E takiuru ana…",
  "admin.use_token_instead": "Whakamahia te tohu kaiwhakahaere",
  "admin.use_credentials_instead": "Whakamahia te ingoa me te kupuhipa",
  "admin.btn_signin": "Uru kaiwhakahaere",
  "admin.dev_token_hint": "Ko te tohu whakawhanake taunoa",
  "admin.list_label": "Kaiwhakahaere · ngana whakahaere",
  "admin.list_title": "Ngā ngana whakahaere",
  "admin.tab_attempts": "Ngana whakahaere",
  "admin.tab_research": "Kaweake raraunga rangahau",
  "admin.tab_bank": "Pūtake Mātauranga",
  "admin.tab_contracts": "Ngā kirimana arotake",
  "admin.tab_engine": "Tirohanga pūkaha",
  "admin.engine_title": "Tirohanga pūkaha · pūrere whakatauira ora",
  "admin.contracts_title": "Ngā kirimana arotake · ngā mahi whanonga, ngā takahanga, ngā ture",
  "admin.research_title": "Kaweake raraunga rangahau",
  "admin.tab_db": "Pātengi raraunga",
  "admin.tab_candidates": "Kaitono",
  "admin.tab_licenses": "Raihana",
  "admin.bank_title": "Pūtake Mātauranga · ngā pātai whiringa-maha",
  "admin.download_all_attempts_xlsx": "Tikiake ngana katoa (Excel)",
  "bank.subtitle": "Ngā kaupapa 36 katoa me ā rātou pātai. Whakatika i tētahi mea, tāpiri pātai hou, whakakore rānei i ngā tāpiringa ritenga. Ka whakaaturia te DS-IN-01 hei pānui-anake nā te mea ka tāriri mai i ngā whaiaro mō ia whenua.",
  "bank.questions": "ngā pātai",
  "bank.profile_locked": "Ka tāriri te DS-IN-01 mai i ngā whaiaro mō ia whenua (UK / Hōrana / Aotearoa / UAE / taunoa). Whakatikahia ngā whaiaro i te waehere hei whakarerekē — kua kati ki konei kia kore ai e rerekē te horopaki o te kaitono i waenganui i te ngana.",
  "bank.edit": "Whakatika",
  "bank.delete": "Whakakore",
  "bank.add_question": "Tāpirihia he pātai hou",
  "bank.question_text": "Kupu pātai",
  "bank.options_label": "Ngā kōwhiringa (tohua te mea tika)",
  "bank.option": "Kōwhiringa",
  "bank.mark_correct": "Tohua hei whakautu tika",
  "bank.correct_hint": "Pāwhiritia te pātene irirangi i te taha o te kōwhiringa tika.",
  "bank.save": "Tiaki",
  "bank.cancel": "Whakakore",
  "bank.saved": "Kua tiakina te pātai",
  "bank.deleted": "Kua whakakorea te pātai",
  "bank.confirm_delete": "Whakakorehia tēnei pātai? Kāore e taea te whakahoki mō ngā pātai hou; ka whakakorea ngohengohe ngā pātai taunoa, ka taea te whakahoki mai i te pātengi.",
  "bank.err_q": "Me whakaurua he kupu pātai",
  "bank.err_options": "Me whakaki ngā kōwhiringa e whā katoa",
  "bank.download_xlsx": "Tikiake ngā pātai katoa (Excel)",
  "admin.refresh": "Whakahōuhia",
  "admin.signout": "Takiputa",
  "admin.download_model_answers": "Tikiake ngā whakautu tauira (.docx)",
  "admin.download_model_answers_open": "Whakatuwhera ki tāpā hou",
  "admin.download_model_answers_busy": "E whakareri ana…",
  "admin.download_model_answers_ok": "Kua tīmata te tikiake whakautu tauira — tirohia tō kōpaki Tikiake",
  "admin.download_model_answers_failed": "Kāore i taea te tikiake i ngā whakautu tauira",
  "admin.invalid_token": "Tohu kaiwhakahaere hē",
  "admin.no_attempts": "Kāore anō he ngana.",
  "admin.loading": "E utaina ana…",
  "admin.select_attempt": "Kōwhiria he ngana ki te taha mauī.",
  "admin.approval_toggled": "Kua hurihia te whakaaetanga",
  "admin.in_progress": "kei te haere",
  "admin.completed": "kua oti",
  "admin.started": "i tīmata",
  "admin.step": "hīkoi",
  "admin.approved": "KUA WHAKAAETIA",
  "admin.btn_approve": "Whakaae",
  "admin.btn_revoke": "Kua whakaaetia (pāwhiri ki te whakakāhore)",
  "admin.btn_delete": "Whakakore",
  "admin.confirm_delete_attempt": "Whakakorehia te ngana a {name}? Ka mukua mō ake ake te ngana ME tana raihana. Kāore e taea te whakahoki.",
  "admin.deleted": "Kua whakakorea te ngana",
  "admin.deleted_with_license": "Kua whakakorea te ngana me te raihana",
  "admin.download_attempt_pdf": "Tikiake PDF ngana",
  "admin.download_attempt_pdf_open": "Whakatuwhera ki tāpā hou",
  "admin.download_attempt_pdf_ok": "Kua tīmata te tikiake PDF ngana — tirohia tō kōpaki Tikiake",
  "admin.download_attempt_pdf_failed": "Kāore i taea te tikiake i te PDF ngana",
  "admin.download_license_pdf": "Tikiake PDF raihana",
  "admin.scoring_title": "Aromatawai ia kaupapa (taunakitanga whanonga)",
  "admin.scoring_sub": "Ka aromatawaihia ia kaupapa mai i ngā mahi ka kitea i roto i te whakatauira — kaua mai i ngā whakautu kupu noa.",
  "admin.event_log": "Rārangi whanonga",
  "admin.review_title": "Tuku kaitono vs whakautu tauira",
  "admin.review_q": "Mōhiotanga · P{n}",
  "admin.review_model": "TAUIRA",
  "admin.review_candidate": "KAITONO",
  "admin.review_correct": "KAITONO · TIKA",
  "admin.cap_expected": "Āheinga · whanonga e tūmanakohia ana",
  "admin.cap_valid_finals": "Ngā whakatau whakamutunga whaimana:",
  "admin.cap_candidate_final": "Whakatau whakamutunga a te kaitono:",
  "admin.cap_critical": "Ngā whanonga matua:",
  "admin.cap_missing_critical": "Kāore i tutuki ngā mea matua:",
  "admin.cap_auto_fail": "Ngā mea ka aukati aunoa:",
  "admin.cap_candidate_triggered": "I whakaohohia e te kaitono:",
  "admin.refl_themes": "Whakaaroaro · ngā kaupapa whakaritea",
  "admin.refl_synonyms": "kupu rite ka whakaaetia:",
  "admin.refl_candidate_text": "Whakaaroaro a te kaitono",
  "admin.pending": "E TATARI ANA",

  // common
  "common.loading": "E utaina ana…",

  // V2 primitive · iddl_file_manager (CS-TA-04, CS-SB-04)
  "sim.fm.workspace_title": "Wāhi mahi IDDL",
  "sim.fm.items": "ngā mea",
  "sim.fm.empty_folder": "Kāore he kōnae i tēnei kōpaki.",
  "sim.fm.select_a_file": "Tīpakohia he kōnae i te rārangi kia tirohia, kia whakarōpūtia, kia neke rānei.",
  "sim.fm.preview": "Arokite kōnae",
  "sim.fm.owner": "Rangatira",
  "sim.fm.no_content": "(kāore he arokite e wātea ana)",
  "sim.fm.click_inspect": "Pāwhiritia Tirohia hei whakaatu i te ihirangi o te kōnae.",
  "sim.fm.btn_inspect": "Tirohia te ihirangi",
  "sim.fm.btn_move": "Neke…",
  "sim.fm.btn_share": "Tiri / kawe atu…",
  "sim.fm.btn_rename": "Whakaingoa anō",
  "sim.fm.classify_as": "Whakarōpūhia te ngāwaritanga",
  "sim.fm.final_decision": "Whakatau whakamutunga",
  "sim.fm.btn_submit": "Tukuna ngā whakarōpū me ngā tūnga whakamutunga",
  "sim.fm.finalized": "Kua tukuna: {decision}.",
  "sim.fm.move_to_label": "Neke \"{file}\" ki tēhea kōpaki?",
  "sim.fm.share_label": "Tiri, kawe atu rānei \"{file}\" — tīpako he wāhi haere",
  "sim.fm.rename_label": "Whakaingoa anō i te kōnae kua tīpakohia",
  "sim.fm.save": "Tiaki",
  "sim.fm.cancel": "Whakakore",
  "sim.fm.unsafe_action_logged": "Mahi haumaru-kore kua rēhita: \"{file}\" → {destination}. Ka rahua tēnei ariā.",
  "sim.fm.share_completed": "Kua tiria ki {destination}.",

  // V2 primitive · iddl_settings (CS-SB-01, CS-SB-03)
  "sim.set.workspace_title": "Tautuhinga pūrere me te pūkete",
  "sim.set.summary_title": "Te oranga o ngā tautuhinga",
  "sim.set.unsafe_count": "{n} tautuhinga haumaru-kore i tēnei whirihoranga",
  "sim.set.all_secure": "He haumaru ngā tautuhinga katoa",
  "sim.set.unsafe_tag": "HAUMARU-KORE",
  "sim.set.on": "Ka",
  "sim.set.off": "Kāo",
  "sim.set.warning_title": "He uara haumaru-kore tēnei",
  "sim.set.warning_default": "Ka whakangohehia e tēnei tautuhinga te tūnga haumaru o tō pūkete, o tō pūrere rānei. Ka taea te pupuri me te whakaae, ka taea rānei te whakahoki ki tētahi uara haumaru i mua i te tuku.",
  "sim.set.warning_revert": "Hoki ki tētahi uara haumaru",
  "sim.set.warning_ack": "E mōhio ana au — kia mau tonu",
  "sim.set.btn_submit": "Tukuna te whirihoranga whakamutunga",
  "sim.set.finalized": "Kua tukuna: {decision}.",

  // V2 primitive · iddl_workflow (CS-IR-01..04)
  "sim.wf.alerts_title": "Ngā whakatūpato kua tae mai",
  "sim.wf.alert_flagged": "KUA TOHUA · raruraru",
  "sim.wf.alert_dismissed": "KUA WHAKAKĀHORETIA · haunga",
  "sim.wf.btn_flag_incident": "Tohua hei raruraru",
  "sim.wf.btn_dismiss": "Whakakāhoretia · ehara i te raruraru",
  "sim.wf.plan_title": "Mahere whakautu",
  "sim.wf.plan_count": "{n} hipa kei te mahere",
  "sim.wf.unsafe_count": "{n} hipa haumaru-kore",
  "sim.wf.palette_title": "Ngā mahi e wātea ana",
  "sim.wf.queue_title": "Tō mahere (i te raupapa)",
  "sim.wf.queue_empty": "Kāore he mahere. Tāpiritia he hipa mai i te papa mahi.",
  "sim.wf.kind_unsafe": "HAUMARU-KORE",
  "sim.wf.warning_title": "He hipa haumaru-kore tēnei",
  "sim.wf.warning_default": "Ka whakangohehia tō whakautu e tēnei mahi. Ka taea te pupuri me te whakaae, ka taea rānei te tango i mua i te tuku.",
  "sim.wf.warning_remove": "Tangohia tēnei hipa",
  "sim.wf.warning_ack": "E mōhio ana au — kia mau tonu",
  "sim.wf.warning_revert_channel": "Hurihia ki te ara haumaru",
  "sim.wf.channel_warning_default": "Ka whakaaturia te raruraru ki te ao mā te pūrongo i tētahi ara tūmatanui, kāore i whakamanahia rānei. Hurihia ki te ara SOC ā-roto.",
  "sim.wf.escalate_title": "Ara whakapikinga",
  "sim.wf.safe_state_summary": "He pai te āhua o te mahere whakamutunga",
  "sim.wf.unsafe_state_summary": "Kei te kī tonu te mahere i ngā hipa haumaru-kore, te ara haumaru-kore rānei",
  "sim.wf.btn_submit": "Tukuna te mahere whakamutunga",
  "sim.wf.finalized": "Kua tukuna: {decision}.",

  // V2 primitive · iddl_editor (DS-CM-01, DS-CM-04)
  "sim.ed.original_title": "Karere taketake",
  "sim.ed.composer_title": "Tito",
  "sim.ed.signals_title": "Ngā tohu reo",
  "sim.ed.signals_none": "Kāore he tohu reo mō tēnei pakaru.",
  "sim.ed.from": "Mai i",
  "sim.ed.to": "Ki",
  "sim.ed.cc": "Tārua",
  "sim.ed.subject": "Kaupapa",
  "sim.ed.body_placeholder": "Tuhia tāu karere ki konei…",
  "sim.ed.warning_default": "He kupu haumaru-kore tēnei i tēnei horopaki. Ka taea te whakatika, te whakaae rānei i mua i te tuku.",
  "sim.ed.warning_ack": "E mōhio ana au — kia mau tonu",
  "sim.ed.unsatisfied_count": "{n} tohu reo kāore anō kia oti",
  "sim.ed.draft_ready": "He pai te tauira tuhinga",
  "sim.ed.btn_send": "Tukuna te karere",
  "sim.ed.finalized": "Kua tukuna: {decision}.",
};

const TABLES = { en: EN, ar: AR, mi: MI };
const STORAGE_KEY = "iddl.lang";

const Ctx = createContext({
  lang: "en",
  dir: "ltr",
  t: (k) => k,
  setLang: () => {},
});

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) || "en"; } catch { return "en"; }
  });

  const setLang = useCallback((next) => {
    setLangState(next);
    try { localStorage.setItem(STORAGE_KEY, next); } catch { /* noop */ }
  }, []);

  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
  }, [lang]);

  const t = useCallback((key, vars) => {
    const table = TABLES[lang] || EN;
    let out = table[key];
    if (out === undefined) out = EN[key] !== undefined ? EN[key] : key;
    if (vars) {
      for (const [k, v] of Object.entries(vars)) {
        out = out.replace(new RegExp(`\\{${k}\\}`, "g"), String(v));
      }
    }
    return out;
  }, [lang]);

  const value = useMemo(
    () => ({ lang, dir: lang === "ar" ? "rtl" : "ltr", t, setLang }),
    [lang, t, setLang],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useT() { return useContext(Ctx); }
